import React, { useCallback, useEffect, useState } from 'react';

import { ContentSkeleton } from 'src/components/ContentSkeleton';
import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import { AdminPlatformUser, formatKycStatusLabel } from 'src/api/adminUsers';
import {
  KycRecord,
  fetchKycRecord,
  fetchKycRecordsForUser,
  formatProviderLabel,
  kycErrorMessage,
} from 'src/api/adminKyc';
import { convertDate } from 'src/components/Table/helpers';
import KycRecordBody from 'src/pages/Kyc/KycRecordBody';
import KycReviewActions from 'src/pages/Kyc/KycReviewActions';
import {
  HeadlineRow,
  MetaLine,
  StatusBadge,
  Title,
  TitleGroup,
} from 'src/pages/Kyc/styled';

import { EmptyState, TabPanel } from '../styled';

type KycTabProps = {
  userId: string;
  user?: AdminPlatformUser | null;
  onForbidden?: () => void;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const { date, time } = convertDate(String(value));
  return `${date} - ${time}`;
};

const KycTab: React.FC<KycTabProps> = ({ userId, user, onForbidden }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState<KycRecord | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Records come back newest first, so the head is the live one.
      const records = await fetchKycRecordsForUser(userId);
      setRecord(records[0] ?? null);
    } catch (e: any) {
      if (e?.response?.status === 403) {
        onForbidden?.();
        return;
      }
      dispatch(
        setAppAlert({ message: kycErrorMessage(e, 'Failed to load KYC records'), isSuccess: false }),
      );
      setRecord(null);
    } finally {
      setLoading(false);
    }
  }, [userId, dispatch, onForbidden]);

  useEffect(() => {
    load();
  }, [load]);

  // A list row carries the owner as a summary object; the detail endpoint is
  // the only place the full Veriff block is guaranteed to be present.
  const handleUpdated = async (updated?: KycRecord) => {
    if (updated) {
      setRecord(updated);
      return;
    }
    if (!record) return;
    try {
      setRecord(await fetchKycRecord(record.id));
    } catch {
      await load();
    }
  };

  if (loading) {
    return (
      <TabPanel>
        <ContentSkeleton variant="fields" count={6} />
      </TabPanel>
    );
  }

  if (!record) {
    return (
      <TabPanel>
        <EmptyState>No KYC records for this user.</EmptyState>
      </TabPanel>
    );
  }

  const status = String(record.status ?? '').toUpperCase();

  return (
    <TabPanel>
      <HeadlineRow>
        <TitleGroup>
          <Title>
            Identity verification
            <StatusBadge $status={status}>{formatKycStatusLabel(status)}</StatusBadge>
            <StatusBadge $status={String(record.provider ?? '')}>
              {formatProviderLabel(record.provider)}
            </StatusBadge>
            {record.requires_manual_review ? (
              <StatusBadge $status="needs review">Needs review</StatusBadge>
            ) : null}
          </Title>
          <MetaLine>
            Created {formatDateTime(record.created_at)} · Updated {formatDateTime(record.updated_at)}
          </MetaLine>
        </TitleGroup>
      </HeadlineRow>

      <KycRecordBody record={record} owner={user} />
      <KycReviewActions record={record} onUpdated={handleUpdated} />
    </TabPanel>
  );
};

export default KycTab;
